<template>
  <v-container fluid grid-list-xs>

    <ConfirmModal
      v-model="showEditJsonConfirm"
      title="Edit JSON Directly"
      message="Are you sure you want to manually edit the JSON? Malformed JSON, misspelled keys, or unsupported Values may cause unexpected behaviors."
      @confirmed="showAdvancedCustom = true"
    ></ConfirmModal>
    <ConfirmModal
      v-model="showEditPermissionsJsonConfirm"
      title="Edit JSON Directly"
      message="Are you sure you want to manually edit the JSON? Malformed JSON, misspelled keys, or unsupported Values may cause unexpected behaviors."
      @confirmed="showAdvancedPermissions = true"
    ></ConfirmModal>

    <!-- Header -->
    <v-layout row wrap mb-2 class="grey lighten-4">
      <v-flex>
        <h2>
          <router-link to="/users">Users</router-link>
          <span class="grey--text">&gt;</span>
          {{ user.name }} <span class="grey--text">(</span>{{ user.id }}<span class="grey--text">)</span>
        </h2>
      </v-flex>

      <v-flex v-if="userLoaded" class="text-sm-right" shrink>
        <v-btn color="error" @click="setStatus(0)" round small v-if="user.enabled">Disable</v-btn>
        <v-btn color="success" @click="setStatus(1)" round small v-if="!user.enabled">Enable</v-btn>
        <v-btn :disabled="!user.enabled || !user.locked" class="success darken-3" @click="setUnlock" round small>
          <span><v-icon v-if="user.locked" small class="mr-2">fa-unlock</v-icon></span>Unlock User
        </v-btn>
        <v-btn color="primary" outline @click="resetPassword" round small>Reset Password</v-btn>
      </v-flex>
    </v-layout>

    <!-- Read-only status info -->
    <v-layout row wrap>
      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Temporary Password:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 class="pa-1">{{ user.temporary_password ? 'Yes' : 'No' }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Password Last Changed:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 class="pa-1">{{ datetime(user.password_last_change) }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Enabled:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 :class="(!user.enabled) ? 'error pa-1' : 'pa-1'">{{ user.enabled ? 'Yes' : 'No' }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Account Locked:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 :class="(user.locked) ? 'error pa-1' : 'pa-1'">{{ user.locked ? 'Yes' : 'No' }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Failed Logins:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 :class="(user.failed_logins > 5) ? 'error pa-1' : 'pa-1'">{{ user.failed_logins }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Last Login:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 class="pa-1">{{ datetime(user.last_login) }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Created:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 class="pa-1">{{ datetime(user.created) }}</v-flex>

      <v-flex xs6 sm2 class="primary--text pa-1 font-weight-black">Modified:</v-flex>
      <v-flex v-if="userLoaded" xs6 sm4 class="pa-1">{{ datetime(user.modified) }}</v-flex>
    </v-layout>

    <v-divider class="primary"></v-divider>

    <v-form v-model="isFormValid" ref="form" @submit.prevent>
      <v-expansion-panel
        v-model="panel"
        expand
        multiple
        class="elevation-0"
      >

        <!-- Basic Information Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">Basic Information</div>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Name"
                    v-model="fields.name"
                    box
                    :class="hasFieldChanged('name') ? 'form__input--modified' : ''"
                    required
                    :rules="[ v => !!v || 'Name must be provided' ]"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-text-field
                    label="Email"
                    v-model="fields.email"
                    box
                    :class="hasFieldChanged('email') ? 'form__input--modified' : ''"
                    required
                    :rules="[ v => !!v || 'Email must be provided', rules.isEmailValid ]"
                  ></v-text-field>
                </v-flex>

                <v-flex xs12 sm6 lg4>
                  <v-combobox
                    label="Position"
                    v-model="fields.position"
                    :items="['User', 'Owner', 'Admin', 'Agent', 'Account Rep']"
                    box
                    :class="hasFieldChanged('position') ? 'form__input--modified' : ''"
                    :rules="[ v => !!v || 'Position must be provided' ]"
                  ></v-combobox>
                </v-flex>

                <v-flex>
              <v-text-field
                label="Password"
                v-model="fields.password"
                box
                :class="hasFieldChanged('password') ? 'form__input--modified' : ''"
                :type="showPassword ? 'text' : 'password'"
                :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                @click:append="showPassword = !showPassword"
                :hint="user.id ? 'Leave blank to keep current password' : 'Min 8 chars, uppercase, lowercase, number'"
                persistent-hint
                :rules="[ v => !!user.id || !!v || 'Password must be provided' ]"
              ></v-text-field>
            </v-flex>
            <v-flex shrink>
              <v-btn small outline color="primary" @click="generateKsuidPassword">Generate Password</v-btn>
            </v-flex>

              </v-layout>

              <v-layout row wrap justify-end>
                <v-btn color="primary" outline @click.stop="reset" :disabled="!submitDisabled">Reset</v-btn>
                <v-btn color="primary" @click.stop="save" :disabled="!submitDisabled" :loading="isProcessing">Save</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <!-- Accounts Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">
            Accounts ({{ userAccounts.length }})
          </div>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap align-center mb-3>
                <v-flex>
                  <span class="subheading">Manage account associations for this user</span>
                </v-flex>
                <v-flex shrink>
                  <v-btn small color="primary" @click="showLinkAccountModal = true">
                    <v-icon small left>fa-plus</v-icon>
                    Link Account
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="accountHeaders"
                :items="userAccounts"
                :loading="loadingAccounts"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <td>{{ props.item.account_id }}</td>
                  <td>{{ props.item.account_name }}</td>
                  <td>{{ props.item.account_email }}</td>
                  <td>{{ props.item.contact_name }}</td>
                  <td>
                    <v-chip small :color="props.item.active ? 'success' : 'error'" text-color="white">
                      {{ props.item.active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </td>
                  <td class="text-xs-right">
                    <v-btn small flat color="error" @click="unlinkAccount(props.item)">
                      <v-icon small left>fa-unlink</v-icon>
                      Unlink
                    </v-btn>
                  </td>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" type="info">
                    No accounts linked to this user
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <!-- Terminal Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">
            Terminals ({{ userTerminals.length }})
          </div>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap align-center mb-3>
                <v-flex>
                  <span class="subheading">Manage terminal access for this user</span>
                </v-flex>
                <v-flex shrink>
                  <v-btn small color="primary" @click="showAddTerminalModal = true" :disabled="!userAccounts.length">
                    <v-icon small left>fa-plus</v-icon>
                    Add Terminal
                  </v-btn>
                </v-flex>
              </v-layout>

              <v-data-table
                :headers="terminalHeaders"
                :items="userTerminals"
                :loading="loadingTerminals"
                hide-actions
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <td>{{ props.item.terminal_id }}</td>
                  <td>{{ props.item.terminal_name }}</td>
                  <td>{{ props.item.terminal_code }}</td>
                  <td>{{ props.item.account_name }}</td>
                  <td>
                    <v-checkbox
                      :input-value="props.item.is_default"
                      @change="updateTerminalFlag(props.item, 'is_default', $event)"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-checkbox
                      :input-value="props.item.can_charge"
                      @change="updateTerminalFlag(props.item, 'can_charge', $event)"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </td>
                  <td>
                    <v-chip small :color="props.item.active ? 'success' : 'error'" text-color="white">
                      {{ props.item.active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </td>
                  <td class="text-xs-right">
                    <v-btn small flat color="error" @click="removeTerminal(props.item)">
                      <v-icon small left>fa-trash</v-icon>
                      Delete
                    </v-btn>
                  </td>
                </template>
                <template v-slot:no-data>
                  <v-alert :value="true" type="info">
                    No terminals assigned to this user
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <!-- Reports Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">Reports</div>
          <ReportsPanel
            v-if="userLoaded"
            :userId="user.id"
            :userAccounts="userAccounts"
            ref="reportsPanel"
          />
        </v-expansion-panel-content>

        <!-- HPP Receipts Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">HPP Receipts</div>
          <ReceiptsPanel
            v-if="userLoaded"
            :userId="user.id"
            :userAccounts="userAccounts"
            ref="receiptsPanel"
          />
        </v-expansion-panel-content>

        <!-- Permissions Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">Permissions</div>
          <v-card flat>
            <v-card-text>
              <v-layout row wrap mb-2>
                <v-flex xs12>
                  <p class="caption grey--text mb-2">Selecting a preset will overwrite both Permissions and Custom Settings.</p>
                  <v-radio-group v-model="selectedPreset" row hide-details @change="onPresetChange" class="mt-0">
                    <v-radio label="Standard User" value="standard" color="primary"></v-radio>
                    <v-radio label="BlueYonder User" value="blueyonder" color="primary"></v-radio>
                    <v-radio label="Admin User" value="admin" color="primary"></v-radio>
                    <v-radio label="Copy Owner" value="owner" color="primary" :disabled="!selectedAccountId || loadingOwnerPerms"></v-radio>
                  </v-radio-group>
                  <v-btn small outline color="primary" class="mt-2" @click="applyPreset('clear'); selectedPreset = null">Clear All</v-btn>
                </v-flex>
              </v-layout>

              <!-- Simple Permissions View -->
              <v-layout row wrap class="pa-2" v-if="!showAdvancedPermissions">
                <v-flex xs12>
                  <v-data-table :headers="columnTableHeaders" :items="activePermissions" hide-actions class="border">
                    <template v-slot:headers="props">
                      <tr>
                        <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                      </tr>
                    </template>
                    <template v-slot:items="props">
                      <td>{{ props.item.label }}</td>
                      <td>{{ props.item.key }}</td>
                      <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                      <td class="text-xs-right">
                        <v-btn flat small color="error" @click="togglePermission(props.item.key, false)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                      </td>
                    </template>
                    <template v-slot:no-data><span class="grey--text pa-2">No permissions enabled</span></template>
                  </v-data-table>
                  <v-layout row align-center mt-2>
                    <v-flex>
                      <v-select v-model="pendingPermission" :items="remainingPermissions" item-text="label" item-value="key" label="Add Permission" clearable hide-details box>
                        <template v-slot:item="data">
                          <v-list-tile-content>
                            <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                            <v-list-tile-sub-title>{{ data.item.key }}</v-list-tile-sub-title>
                          </v-list-tile-content>
                        </template>
                      </v-select>
                    </v-flex>
                    <v-flex shrink>
                      <v-btn small color="primary" :disabled="!pendingPermission" @click="togglePermission(pendingPermission, true); pendingPermission = null"><v-icon small>fa-plus</v-icon></v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>
                <v-flex xs12 mt-3>
                  <v-layout row align-center>
                    <h4 class="pa-2 grey--text text--darken-2">Preview</h4>
                    <v-btn small outline color="primary" @click="confirmEditPermissionsJson">Edit JSON Directly</v-btn>
                  </v-layout>
                  <pre class="custom-json-preview">{{ JSON.stringify(permissionsObj, null, 2) }}</pre>
                </v-flex>
              </v-layout>

              <!-- Advanced Permissions View (JSON) -->
              <v-layout row wrap class="pa-2" v-else>
                <v-flex xs12>
                  <v-layout row align-center mb-1>
                    <h4 class="pa-2 grey--text text--darken-2">Permissions (JSON)</h4>
                    <v-btn small outline color="primary" @click="showAdvancedPermissions = false">Simple View</v-btn>
                  </v-layout>
                  <v-textarea
                    v-model="fields.permissions"
                    box
                    :class="hasFieldChanged('permissions') ? 'form__input--modified' : ''"
                    :rules="[ rules.validJson, rules.validPermissionsKeys ]"
                    rows="16"
                    auto-grow
                  ></v-textarea>
                </v-flex>
              </v-layout>

              <v-layout row wrap justify-end>
                <v-btn color="primary" outline @click.stop="reset" :disabled="!submitDisabled">Reset</v-btn>
                <v-btn color="primary" @click.stop="save" :disabled="!submitDisabled" :loading="isProcessing">Save</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

        <!-- Custom Settings Panel -->
        <v-expansion-panel-content>
          <div slot="header" class="title font-weight-medium">Custom Settings</div>
          <v-card flat>
            <v-card-text>

              <!-- Simple Custom Settings View -->
              <v-layout row wrap class="pa-2" v-if="!showAdvancedCustom">
                <v-flex xs12>
                  <h3 class="pa-2 primary--text headline">Custom Pagination Setting</h3>
                </v-flex>
                <v-flex xs12 mb-3>
                  <v-text-field
                    label="Rows Per Page"
                    style="max-width: 200px"
                    :value="customObj.pagination && customObj.pagination.rowsPerPage"
                    @input="setCustomRowsPerPage($event)"
                    type="number"
                    hide-details
                    box
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 mt-3>
                  <h3 class="pa-2 primary--text headline">Custom Card Settings</h3>
                </v-flex>
                <v-layout row wrap class="border pa-3">
                  <v-flex xs12 sm6 pa-3>
                    <h4 class="pa-2 grey--text text--darken-2 title">Card Transaction Columns</h4>
                    <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_columns')" hide-actions class="border">
                      <template v-slot:headers="props">
                        <tr>
                          <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                        </tr>
                      </template>
                      <template v-slot:items="props">
                        <td>{{ props.item.label }}</td>
                        <td>{{ props.item.value }}</td>
                        <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                        <td class="text-xs-right">
                          <v-btn flat small color="error" @click="removeColumnFromField('transaction_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                        </td>
                      </template>
                      <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                    </v-data-table>
                    <v-layout row align-center mt-2>
                      <v-flex>
                        <v-select v-model="pendingColumn.transaction_columns" :items="remainingColumnsFor('transaction_columns')" item-text="label" item-value="value" label="Add Card Transaction Column" clearable hide-details box>
                          <template v-slot:item="data">
                            <v-list-tile-content>
                              <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                              <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                            </v-list-tile-content>
                          </template>
                        </v-select>
                      </v-flex>
                      <v-flex shrink>
                        <v-btn small color="primary" :disabled="!pendingColumn.transaction_columns" @click="addColumnToField('transaction_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>

                  <v-flex xs12 sm6 pa-3>
                    <h4 class="pa-2 grey--text text--darken-2 title">Card Transaction Export Columns</h4>
                    <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_export_columns')" hide-actions class="border">
                      <template v-slot:headers="props">
                        <tr>
                          <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                        </tr>
                      </template>
                      <template v-slot:items="props">
                        <td>{{ props.item.label }}</td>
                        <td>{{ props.item.value }}</td>
                        <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                        <td class="text-xs-right">
                          <v-btn flat small color="error" @click="removeColumnFromField('transaction_export_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                        </td>
                      </template>
                      <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                    </v-data-table>
                    <v-layout row align-center mt-2>
                      <v-flex>
                        <v-select v-model="pendingColumn.transaction_export_columns" :items="remainingColumnsFor('transaction_export_columns')" item-text="label" item-value="value" label="Add Card Transaction Export Column" clearable hide-details box>
                          <template v-slot:item="data">
                            <v-list-tile-content>
                              <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                              <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                            </v-list-tile-content>
                          </template>
                        </v-select>
                      </v-flex>
                      <v-flex shrink>
                        <v-btn small color="primary" :disabled="!pendingColumn.transaction_export_columns" @click="addColumnToField('transaction_export_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>

                </v-layout>
                <v-flex xs12 mt-3>
                  <h3 class="pa-2 primary--text headline">Custom ACH settings</h3>
                </v-flex>
                <v-layout row wrap class="border pa-3">
                  <v-flex xs12 sm6 pa-3>
                    <h4 class="pa-2 grey--text text--darken-2 title">ACH Transaction Columns</h4>
                    <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_ach_columns')" hide-actions class="border">
                      <template v-slot:headers="props">
                        <tr>
                          <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                        </tr>
                      </template>
                      <template v-slot:items="props">
                        <td>{{ props.item.label }}</td>
                        <td>{{ props.item.value }}</td>
                        <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                        <td class="text-xs-right">
                          <v-btn flat small color="error" @click="removeColumnFromField('transaction_ach_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                        </td>
                      </template>
                      <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                    </v-data-table>
                    <v-layout row align-center mt-2>
                      <v-flex>
                        <v-select v-model="pendingColumn.transaction_ach_columns" :items="remainingColumnsFor('transaction_ach_columns')" item-text="label" item-value="value" label="Add ACH Transaction Column" clearable hide-details box>
                          <template v-slot:item="data">
                            <v-list-tile-content>
                              <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                              <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                            </v-list-tile-content>
                          </template>
                        </v-select>
                      </v-flex>
                      <v-flex shrink>
                        <v-btn small color="primary" :disabled="!pendingColumn.transaction_ach_columns" @click="addColumnToField('transaction_ach_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>

                  <v-flex xs12 sm6 pa-3>
                    <h4 class="pa-2 grey--text text--darken-2 title">ACH Transaction Export Columns</h4>
                    <v-data-table :headers="columnTableHeaders" :items="activeColumnsFor('transaction_ach_export_columns')" hide-actions class="border">
                      <template v-slot:headers="props">
                        <tr>
                          <th v-for="header in props.headers" :key="header.value" class="primary white--text subheading" :class="header.align ? `text-xs-${header.align}` : ''">{{ header.text }}</th>
                        </tr>
                      </template>
                      <template v-slot:items="props">
                        <td>{{ props.item.label }}</td>
                        <td>{{ props.item.value }}</td>
                        <td><v-chip small color="success" text-color="white">Enabled</v-chip></td>
                        <td class="text-xs-right">
                          <v-btn flat small color="error" @click="removeColumnFromField('transaction_ach_export_columns', props.item.value)"><v-icon small left>fa-trash</v-icon> Delete</v-btn>
                        </td>
                      </template>
                      <template v-slot:no-data><span class="grey--text pa-2">No columns selected</span></template>
                    </v-data-table>
                    <v-layout row align-center mt-2>
                      <v-flex>
                        <v-select v-model="pendingColumn.transaction_ach_export_columns" :items="remainingColumnsFor('transaction_ach_export_columns')" item-text="label" item-value="value" label="Add ACH Transaction Export Column" clearable hide-details box>
                          <template v-slot:item="data">
                            <v-list-tile-content>
                              <v-list-tile-title>{{ data.item.label }}</v-list-tile-title>
                              <v-list-tile-sub-title>{{ data.item.value }}</v-list-tile-sub-title>
                            </v-list-tile-content>
                          </template>
                        </v-select>
                      </v-flex>
                      <v-flex shrink>
                        <v-btn small color="primary" :disabled="!pendingColumn.transaction_ach_export_columns" @click="addColumnToField('transaction_ach_export_columns')"><v-icon small>fa-plus</v-icon></v-btn>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                </v-layout>
                <v-flex xs12 mt-3>
                  <v-layout row align-center>
                    <h4 class="pa-2 grey--text text--darken-2">Preview</h4>
                    <v-btn small outline color="primary" @click="confirmEditJson">Edit JSON Directly</v-btn>
                  </v-layout>
                  <pre class="custom-json-preview">{{ JSON.stringify(customObj, null, 2) }}</pre>
                </v-flex>
              </v-layout>

              <!-- Advanced Custom Settings View (JSON) -->
              <v-layout row wrap class="pa-2" v-else>
                <v-flex xs12>
                  <v-layout row align-center mb-1>
                    <h4 class="pa-2 grey--text text--darken-2">Custom Settings (JSON)</h4>
                    <v-btn small outline color="primary" @click="showAdvancedCustom = false">Simple View</v-btn>
                  </v-layout>
                  <v-textarea
                    v-model="fields.custom"
                    box
                    :class="hasFieldChanged('custom') ? 'form__input--modified' : ''"
                    :rules="[ rules.validJson, rules.validCustomKeys ]"
                    rows="12"
                    auto-grow
                  ></v-textarea>
                </v-flex>
              </v-layout>

              <v-layout row wrap justify-end>
                <v-btn color="primary" outline @click.stop="reset" :disabled="!submitDisabled">Reset</v-btn>
                <v-btn color="primary" @click.stop="save" :disabled="!submitDisabled" :loading="isProcessing">Save</v-btn>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-expansion-panel-content>

      </v-expansion-panel>
    </v-form>

    <!-- Link Account Modal -->
    <LinkAccountModal
      v-if="userLoaded"
      v-model="showLinkAccountModal"
      :userId="user.id"
      :existingAccountIds="existingAccountIds"
      @linked="onAccountLinked"
    />

    <!-- Add Terminal Modal -->
    <AddTerminalModal
      v-if="userLoaded"
      v-model="showAddTerminalModal"
      :userId="user.id"
      :userAccounts="userAccounts"
      :existingTerminalIds="existingTerminalIds"
      @added="onTerminalAdded"
    />
  </v-container>
</template>

<script>
import KSUID from 'ksuid'
import Bus from '@/bus'

import LinkAccountModal from '@/components/manage/user/LinkAccountModal'
import AddTerminalModal from '@/components/manage/user/AddTerminalModal'
import ReportsPanel from '@/components/manage/user/ReportsPanel'
import ReceiptsPanel from '@/components/manage/user/ReceiptsPanel'
import ConfirmModal from '@/components/ConfirmModal'

import ApiMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import DisplayMixin from '@/mixins/display'
import StoreMixin from '@/store/mixin'
import UtilityMixin from '@/mixins/utility'

const ALLOWED_USER_PERMISSIONS = new Set([
  'superuser',
  'can_manage_users',
  'can_manage_accounts',
  'can_manage_terminals',
  'can_manage_processors',
  'can_use_virtual_terminal',
  'can_standalone_refund',
  'can_view_transactions',
  'can_manage_transactions',
  'can_view_account_transactions',
  'can_manage_account_transactions',
  'can_view_schedules',
  'can_manage_schedules',
  'can_view_account_schedules',
  'can_manage_account_schedules',
  'can_waive_card_fees',
  'can_waive_health_card_fees',
  'can_refund_ach_without_wait',
  'can_view_funding',
  'can_use_jetpay',
  'can_use_okdmv_renewal',
  'has_installment_options',
  'has_recurring_options',
  'has_schedules'
])

const ALLOWED_CUSTOM_KEYS = new Set([
  'default_account_id',
  'default_card_terminal_id',
  'default_ach_terminal_id',
  'pagination',
  'transaction_columns',
  'transaction_export_columns',
  'transaction_ach_columns',
  'transaction_ach_export_columns',
  'password_grace_period'
])

const ALLOWED_COLUMN_VALUES = new Set([
  'tracking_id',
  'reference_number',
  'customer_name',
  'billing_email',
  'request_type',
  'transactions_source_id',
  'transaction_status',
  'last_four',
  'card_expiration_yymm',
  'payment_type',
  'total_amount',
  'net_amount',
  'less_fee_amount',
  'fee_amount',
  'is_card_present',
  'is_recurring',
  'is_fee_waived',
  'ach_is_authorized',
  'order_number',
  'customer_po',
  'custom_field_1',
  'custom_field_2',
  'custom_field_3',
  'account_name',
  'terminal_name',
  'terminal_code',
  'user_name',
  'user_email',
  'response_code',
  'response_text',
  'ach_rejected',
  'created',
  'settled'
])

export default {
  components: { LinkAccountModal, AddTerminalModal, ReportsPanel, ReceiptsPanel, ConfirmModal },
  mixins: [ApiMixin, AuthMixin, DisplayMixin, StoreMixin, UtilityMixin],

  data: function () {
    return {
      panel: [],
      user: {},
      userLoaded: false,

      // Form state
      isFormValid: false,
      isProcessing: false,
      showPassword: false,
      selectedPreset: null,
      showAdvancedPermissions: false,
      showAdvancedCustom: false,
      showEditJsonConfirm: false,
      showEditPermissionsJsonConfirm: false,
      pendingPermission: null,
      loadingOwnerPerms: false,
      fields: this.defaultFormFields(),
      pendingColumn: {
        transaction_columns: null,
        transaction_export_columns: null,
        transaction_ach_columns: null,
        transaction_ach_export_columns: null
      },

      // Accounts
      userAccounts: [],
      loadingAccounts: false,
      showLinkAccountModal: false,
      accountHeaders: [
        { text: 'ID', value: 'account_id', sortable: false },
        { text: 'Name', value: 'account_name', sortable: false },
        { text: 'Email', value: 'account_email', sortable: false },
        { text: 'Contact', value: 'contact_name', sortable: false },
        { text: 'Status', value: 'active', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],

      // Terminals
      userTerminals: [],
      loadingTerminals: false,
      showAddTerminalModal: false,
      terminalHeaders: [
        { text: 'ID', value: 'terminal_id', sortable: false },
        { text: 'Name', value: 'terminal_name', sortable: false },
        { text: 'Code', value: 'terminal_code', sortable: false },
        { text: 'Account', value: 'account_name', sortable: false },
        { text: 'Default', value: 'is_default', sortable: false },
        { text: 'Can Charge', value: 'can_charge', sortable: false },
        { text: 'Status', value: 'active', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
      ],

      columnTableHeaders: [
        { text: 'Setting', value: 'label', sortable: false },
        { text: 'Setting Key', value: 'key_value', sortable: false },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Action', value: 'actions', sortable: false, align: 'right' }
      ],

      transactionColumnsList: [
        { value: 'tracking_id', label: 'ID' },
        { value: 'reference_number', label: 'Reference ID' },
        { value: 'customer_name', label: 'Card/Acct. Name' },
        { value: 'billing_email', label: 'Cust. Email' },
        { value: 'request_type', label: 'Type' },
        { value: 'transactions_source_id', label: 'Hosted Payment' },
        { value: 'transaction_status', label: 'Status' },
        { value: 'last_four', label: 'Last Four' },
        { value: 'card_expiration_yymm', label: 'Expiration' },
        { value: 'payment_type', label: 'Card Type' },
        { value: 'total_amount', label: 'Amount ($)' },
        { value: 'net_amount', label: 'Net ($)' },
        { value: 'less_fee_amount', label: 'Less Adj ($)' },
        { value: 'fee_amount', label: 'Fee ($)' },
        { value: 'is_card_present', label: 'Card Present' },
        { value: 'is_recurring', label: 'Recurring' },
        { value: 'is_fee_waived', label: 'Fee Waived' },
        { value: 'ach_is_authorized', label: 'Authorized' },
        { value: 'order_number', label: 'Order Number' },
        { value: 'customer_po', label: 'Customer PO' },
        { value: 'custom_field_1', label: 'Custom 1' },
        { value: 'custom_field_2', label: 'Custom 2' },
        { value: 'custom_field_3', label: 'Custom 3' },
        { value: 'account_name', label: 'Account' },
        { value: 'terminal_name', label: 'Terminal Name' },
        { value: 'terminal_code', label: 'Terminal Code' },
        { value: 'user_name', label: 'Created By' },
        { value: 'user_email', label: 'User Email' },
        { value: 'response_code', label: 'Response Code' },
        { value: 'response_text', label: 'Response Text' },
        { value: 'ach_rejected', label: 'Rejected' },
        { value: 'created', label: 'Created' },
        { value: 'settled', label: 'Settled' }
      ],

      permissionsList: [
        { key: 'superuser', label: 'Superuser (Full Access)' },
        { key: 'can_manage_users', label: 'Manage Users' },
        { key: 'can_use_virtual_terminal', label: 'Use Virtual Terminal' },
        { key: 'can_view_transactions', label: 'View Transactions' },
        { key: 'can_manage_transactions', label: 'Manage Transactions' },
        { key: 'can_view_account_transactions', label: 'View Account Transactions' },
        { key: 'can_manage_account_transactions', label: 'Manage Account Transactions' },
        { key: 'can_view_schedules', label: 'View Schedules' },
        { key: 'can_manage_schedules', label: 'Manage Schedules' },
        { key: 'can_view_account_schedules', label: 'View Account Schedules' },
        { key: 'can_manage_account_schedules', label: 'Manage Account Schedules' },
        { key: 'can_waive_card_fees', label: 'Waive Card Fees' },
        { key: 'can_waive_health_card_fees', label: 'Waive Health Card Fees' },
        { key: 'has_installment_options', label: 'Installment Options' },
        { key: 'has_recurring_options', label: 'Recurring Options' },
        { key: 'has_schedules', label: 'Recurring Schedules' },
        { key: 'can_refund_ach_without_wait', label: 'Refund ACH Without Wait' }
      ],

      presets: {
        standard: {
          can_use_virtual_terminal: true,
          can_view_transactions: true,
          can_manage_transactions: true,
          can_view_account_transactions: true,
          can_manage_account_transactions: true,
          can_view_schedules: true,
          can_manage_schedules: true,
          can_view_account_schedules: true,
          can_manage_account_schedules: true,
          can_waive_card_fees: true,
          has_schedules: true
        },
        blueyonder: {
          can_use_virtual_terminal: true,
          can_view_transactions: true,
          can_manage_transactions: true,
          can_view_account_transactions: true,
          can_manage_account_transactions: true,
          can_view_schedules: true,
          can_manage_schedules: true,
          can_view_account_schedules: true,
          can_manage_account_schedules: true,
          can_waive_card_fees: true,
          can_waive_health_card_fees: true,
          has_installment_options: true,
          has_recurring_options: true,
          has_schedules: true
        },
        admin: {
          can_use_virtual_terminal: true,
          can_view_transactions: true,
          can_manage_transactions: true,
          can_view_account_transactions: true,
          can_manage_account_transactions: true,
          can_view_schedules: true,
          can_manage_schedules: true,
          can_view_account_schedules: true,
          can_manage_account_schedules: true,
          can_waive_card_fees: true,
          can_waive_health_card_fees: true,
          has_installment_options: true,
          has_recurring_options: true,
          has_schedules: true,
          can_refund_ach_without_wait: true
        },
        clear: {}
      },

      rules: {
        required: value => !!value || 'Value is required',
        isEmailValid: value => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value) || 'Invalid email address',
        validJson: value => this.isJsonValid(value, true) || 'Invalid JSON',
        validPermissionsKeys: value => {
          try {
            const str = typeof value === 'string' ? value : JSON.stringify(value)

            // Detect duplicate keys before parsing (JSON.parse silently drops them)
            const keyRegex = /"([^"]+)"\s*:/g
            const keys = []
            let match
            while ((match = keyRegex.exec(str)) !== null) {
              keys.push(match[1])
            }
            if (new Set(keys).size !== keys.length) return 'Duplicate keys are not allowed'

            const obj = JSON.parse(str)
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return true
            const hasInvalidKey = Object.keys(obj).some(k => !ALLOWED_USER_PERMISSIONS.has(k))
            const hasInvalidValue = Object.values(obj).some(v => typeof v !== 'boolean')
            return (!hasInvalidKey && !hasInvalidValue) || 'Invalid key or value'
          } catch (e) {
            return true
          }
        },
        validCustomKeys: value => {
          try {
            const str = typeof value === 'string' ? value : JSON.stringify(value)

            // Detect duplicate keys before parsing (JSON.parse silently drops them)
            const keyRegex = /"([^"]+)"\s*:/g
            const keys = []
            let match
            while ((match = keyRegex.exec(str)) !== null) {
              keys.push(match[1])
            }
            if (new Set(keys).size !== keys.length) return 'Duplicate keys are not allowed'

            const obj = JSON.parse(str)
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return true
            const hasInvalidKey = Object.keys(obj).some(k => !ALLOWED_CUSTOM_KEYS.has(k))
            if (hasInvalidKey) return 'Invalid key or value'
            const columnFields = [
              'transaction_columns',
              'transaction_export_columns',
              'transaction_ach_columns',
              'transaction_ach_export_columns'
            ]
            for (const field of columnFields) {
              if (Array.isArray(obj[field]) && obj[field].some(v => !ALLOWED_COLUMN_VALUES.has(v))) {
                return 'Invalid key or value'
              }
            }
            return true
          } catch (e) {
            return true
          }
        }
      }
    }
  },

  computed: {
    existingAccountIds () {
      return this.userAccounts.map(a => a.account_id)
    },
    existingTerminalIds () {
      return this.userTerminals.map(t => t.terminal_id)
    },
    selectedAccountId () {
      return this.userAccounts.length ? this.userAccounts[0].account_id : null
    },
    customObj () {
      try {
        if (typeof this.fields.custom === 'string') {
          return JSON.parse(this.fields.custom) || {}
        }
        return this.fields.custom || {}
      } catch (e) {
        return {}
      }
    },
    activePermissions () {
      return this.permissionsList.filter(p => this.permissionsObj[p.key])
    },
    remainingPermissions () {
      return this.permissionsList.filter(p => !this.permissionsObj[p.key])
    },
    permissionsObj () {
      try {
        if (typeof this.fields.permissions === 'string') {
          return JSON.parse(this.fields.permissions) || {}
        }
        return this.fields.permissions || {}
      } catch (e) {
        return {}
      }
    },
    submitDisabled () {
      if (!this.isFormValid) return true
      for (const field in this.fields) {
        if (this.hasFieldChanged(field)) return true
      }
      return false
    }
  },

  mounted () {
    this.load()
  },

  beforeRouteUpdate (to, from, next) {
    this.reset()
    next()
  },

  watch: {
    $route (to, from) {
      this.reset()
    }
  },

  methods: {
    defaultFormFields () {
      return {
        name: '',
        email: '',
        password: '',
        position: '',
        permissions: '{}',
        custom: JSON.stringify({
          pagination: {
            rowsPerPage: 10
          },
          transaction_columns: [
            'customer_name',
            'request_type',
            'transaction_status',
            'transactions_source_id',
            'last_four',
            'card_expiration_yymm',
            'total_amount',
            'less_fee_amount',
            'is_recurring',
            'custom_field_1',
            'created',
            'settled'
          ],
          transaction_export_columns: [
            'customer_name',
            'request_type',
            'transaction_status',
            'transactions_source_id',
            'last_four',
            'card_expiration_yymm',
            'total_amount',
            'less_fee_amount',
            'is_recurring',
            'customer_po',
            'custom_field_1',
            'custom_field_2',
            'custom_field_3',
            'created',
            'settled'
          ]
        }, null, 2)
      }
    },

    populateFields (userData) {
      this.fields = this.defaultFormFields()
      for (const [key, value] of Object.entries(userData)) {
        if (this.fields.hasOwnProperty(key)) {
          if (key === 'permissions' || key === 'custom') {
            this.fields[key] = this.objectPretty(value)
          } else {
            this.fields[key] = value
          }
        }
      }
    },

    reset () {
      this.showAdvancedPermissions = false
      this.showAdvancedCustom = false
      this.selectedPreset = null
      this.load()
    },

    togglePermission (key, value) {
      let perms = { ...this.permissionsObj }
      if (value) {
        perms[key] = true
      } else {
        delete perms[key]
      }
      this.fields.permissions = JSON.stringify(perms, null, 2)
    },

    onPresetChange (value) {
      if (value === 'owner') {
        this.copyOwnerPermissions()
      } else {
        this.applyPreset(value)
      }
    },

    applyPreset (presetName) {
      const preset = this.presets[presetName]
      this.fields.permissions = JSON.stringify(preset, null, 2)
      this.selectedPreset = presetName
    },

    async copyOwnerPermissions () {
      if (!this.selectedAccountId) {
        Bus.$emit('openModal', { status: 'error', message: 'No account linked to this user' })
        return
      }

      this.loadingOwnerPerms = true
      try {
        let result = await this.api({
          method: 'get',
          url: `/users/accounts/${this.selectedAccountId}/owner-permissions`
        })
        this.fields.permissions = JSON.stringify(result.data.permissions, null, 2)
        this.selectedPreset = 'owner'
        Bus.$emit('openModal', {
          status: 'success',
          message: `Copied permissions from owner: ${result.data.owner_name}`
        })
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (error.response && error.response.data && error.response.data.error) || 'No owner found for this account'
        })
      } finally {
        this.loadingOwnerPerms = false
      }
    },

    hasFieldChanged (field) {
      if (!this.user.id) return false
      if (field === 'custom' || field === 'permissions') {
        return this.fields[field] !== this.objectPretty(this.user[field])
      }
      return this.fields[field] !== this.user[field]
    },

    generateKsuidPassword () {
      this.fields.password = KSUID.randomSync().string
    },

    confirmEditJson () {
      this.showEditJsonConfirm = true
    },

    confirmEditPermissionsJson () {
      this.showEditPermissionsJsonConfirm = true
    },

    toggleColumn (currentCols, column, checked) {
      let cols = Array.isArray(currentCols) ? [...currentCols] : []
      if (checked) {
        if (!cols.includes(column)) cols.push(column)
      } else {
        cols = cols.filter(c => c !== column)
      }
      return cols
    },

    setCustomColumns (key, value) {
      let custom = { ...this.customObj }
      custom[key] = value
      this.fields.custom = JSON.stringify(custom, null, 2)
    },

    activeColumnsFor (field) {
      const active = this.customObj[field] || []
      return this.transactionColumnsList.filter(col => active.includes(col.value))
    },

    remainingColumnsFor (field) {
      const active = this.customObj[field] || []
      return this.transactionColumnsList.filter(col => !active.includes(col.value))
    },

    addColumnToField (field) {
      const value = this.pendingColumn[field]
      if (!value) return
      const current = this.customObj[field] || []
      this.setCustomColumns(field, [...current, value])
      this.pendingColumn[field] = null
    },

    removeColumnFromField (field, value) {
      this.setCustomColumns(field, (this.customObj[field] || []).filter(v => v !== value))
    },

    setCustomRowsPerPage (value) {
      let custom = { ...this.customObj }
      if (!custom.pagination) custom.pagination = {}
      custom.pagination.rowsPerPage = parseInt(value) || 10
      this.fields.custom = JSON.stringify(custom, null, 2)
    },

    async load () {
      try {
        Bus.$emit('openWaitModal')

        if (!this.$route.params.id) {
          throw new Error('Invalid user')
        }

        let data = await this.api({ url: `/users/${this.$route.params.id}` })

        this.user = data.data.user
        this.user.password = ''
        this.userLoaded = true

        this.populateFields(data.data.user)

        await Promise.all([
          this.loadAccounts(),
          this.loadTerminals()
        ])
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: error })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },

    async loadAccounts () {
      this.loadingAccounts = true
      try {
        let result = await this.api({
          method: 'get',
          url: `/users/${this.user.id}/accounts`
        })
        this.userAccounts = result.data.accounts
      } catch (error) {
        console.error('Error loading accounts:', error)
      } finally {
        this.loadingAccounts = false
      }
    },

    async loadTerminals () {
      this.loadingTerminals = true
      try {
        let result = await this.api({
          method: 'get',
          url: `/users/${this.user.id}/terminals`
        })
        this.userTerminals = result.data.terminals
      } catch (error) {
        console.error('Error loading terminals:', error)
      } finally {
        this.loadingTerminals = false
      }
    },

    async save () {
      this.isProcessing = true

      try {
        if (!this.$refs.form.validate()) {
          let errors = []
          this.$refs.form.inputs.forEach(e => {
            if (e.hasError) errors.push(e.errorBucket[0])
          })

          Bus.$emit('openModal', {
            status: 'error',
            message: errors.length > 1 ? 'Validation Errors' : 'Validation Error',
            messageDetails: errors
          })

          return false
        }

        let options = { ...this.fields }

        try {
          options.custom = JSON.parse(options.custom)
        } catch (e) {
          Bus.$emit('openModal', { status: 'error', message: 'Validation Error', messageDetails: 'Invalid JSON for custom field' })
          return false
        }

        try {
          options.permissions = JSON.parse(options.permissions)
        } catch (e) {
          Bus.$emit('openModal', { status: 'error', message: 'Validation Error', messageDetails: 'Invalid JSON for permissions field' })
          return false
        }

        if (!options.password) delete options.password

        if (options.name) options.name = options.name.trim().replace(/\s\s+/g, ' ')
        if (options.email) options.email = options.email.trim().toLowerCase()

        Bus.$emit('openWaitModal')

        let result = await this.api({
          method: 'put',
          url: `/users/${this.user.id}`,
          data: options
        })

        if (result.data.hasOwnProperty('error') && !result.data.error) {
          Bus.$emit('openModal', { status: 'success', message: 'User Saved' })

          let data = await this.api({ url: `/users/${this.$route.params.id}` })
          this.user = data.data.user
          this.user.password = ''
          this.populateFields(data.data.user)
        } else {
          throw new Error('Problem saving user')
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (err.response && err.response.data && err.response.data.error) || err.message || err
        })
      } finally {
        this.isProcessing = false
        Bus.$emit('closeWaitModal')
      }
    },

    async unlinkAccount (account) {
      if (!confirm(`Are you sure you want to unlink "${account.account_name}" from this user? This will also remove all terminal associations and report subscriptions for this account.`)) {
        return
      }

      try {
        Bus.$emit('openWaitModal')
        await this.api({
          method: 'delete',
          url: `/users/${this.user.id}/accounts/${account.account_id}`
        })

        Bus.$emit('openModal', {
          status: 'success',
          message: `Account "${account.account_name}" unlinked successfully`
        })

        await Promise.all([
          this.loadAccounts(),
          this.loadTerminals()
        ])

        if (this.$refs.reportsPanel) this.$refs.reportsPanel.loadData()
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (error.response && error.response.data && error.response.data.error) || 'Error unlinking account'
        })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    },

    async updateTerminalFlag (terminal, flag, value) {
      try {
        let data = {}
        data[flag] = value

        await this.api({
          method: 'put',
          url: `/users/${this.user.id}/terminals/${terminal.terminal_id}`,
          data: data
        })

        terminal[flag] = value ? 1 : 0
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: 'Error updating terminal' })
        this.loadTerminals()
      }
    },

    async removeTerminal (terminal) {
      if (!confirm(`Are you sure you want to remove terminal "${terminal.terminal_name}" from this user?`)) {
        return
      }

      try {
        await this.api({
          method: 'delete',
          url: `/users/${this.user.id}/terminals/${terminal.terminal_id}`
        })

        Bus.$emit('openModal', { status: 'success', message: 'Terminal removed successfully' })
        this.loadTerminals()
      } catch (error) {
        Bus.$emit('openModal', { status: 'error', message: 'Error removing terminal' })
      }
    },

    onAccountLinked () {
      this.loadAccounts()
      this.loadTerminals()
      if (this.$refs.reportsPanel) this.$refs.reportsPanel.loadData()
    },

    onTerminalAdded () {
      this.loadTerminals()
    },

    async setStatus (isEnabled = 1) {
      const subPath = isEnabled === 0 ? 'disable' : 'enable'

      if (confirm(`Are you sure you would like to ${subPath} this user?`)) {
        try {
          Bus.$emit('openWaitModal')

          await this.api({ method: 'put', url: `/users/${this.user.id}/${subPath}` })

          let data = await this.api({ url: `/users/${this.$route.params.id}` })
          this.user = data.data.user
          this.user.password = ''
        } catch (error) {
          console.error('Error updating status', error)
        } finally {
          Bus.$emit('closeWaitModal')
        }
      }
    },

    async setUnlock () {
      if (confirm('Are you sure you would like to unlock this user?')) {
        try {
          Bus.$emit('openWaitModal')

          await this.api({ method: 'put', url: `/users/${this.user.id}/unlock` })

          let data = await this.api({ url: `/users/${this.$route.params.id}` })
          this.user = data.data.user
          this.user.password = ''
        } catch (error) {
          console.error('Error unlocking user', error)
        } finally {
          Bus.$emit('closeWaitModal')
        }
      }
    },

    async resetPassword () {
      if (!confirm('Are you sure you want to reset this user\'s password? They will receive a temporary password.')) {
        return
      }

      try {
        Bus.$emit('openWaitModal')

        let result = await this.api({
          method: 'put',
          url: `/users/${this.user.id}/reset_password`
        })

        Bus.$emit('openModal', {
          status: 'success',
          message: 'Password Reset Successfully',
          messageDetails: [
            `User: ${this.user.name}`,
            `Password: ${result.data.temporary_password}`
          ],
          persist: true
        })

        let data = await this.api({ url: `/users/${this.$route.params.id}` })
        this.user = data.data.user
        this.user.password = ''
      } catch (error) {
        Bus.$emit('openModal', {
          status: 'error',
          message: (error.response && error.response.data && error.response.data.error) || 'Error resetting password'
        })
      } finally {
        Bus.$emit('closeWaitModal')
      }
    }
  }
}
// This is a test comment
</script>

<style lang="less">
  .v-expansion-panel__container {
    margin: 5px;
  }
  .v-expansion-panel__header {
    background: var(--v-secondary-base) !important;
    padding: 5px;
  }

  .error {
    font-weight: 900;
    color: #ffff;
    background-color: var(--v-error-darken1) !important;
  }

  .border {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }

  .form__input--modified .v-input__slot {
    border: 1px solid var(--v-primary-base);
  }

.custom-json-preview {
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 12px;
    font-size: 12px;
    line-height: 1.5;
    overflow-x: auto;
    white-space: pre;
  }
</style>



// WEBPACK FOOTER //
// src/components/manage/User.vue